import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import { Mail, Check, Download } from "lucide-react";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Table } from "../components/ui/Table/Table";
import { Button } from "../components/ui/Button/Button";
import { StatusBadge } from "../components/ui/StatusBadge/StatusBadge";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { Pagination } from "../components/ui/Pagination/Pagination";
import { FilterBar } from "../components/ui/FilterBar/FilterBar";
import { Modal } from "../components/ui/Modal/Modal";
import { FormField } from "../components/ui/FormField/FormField";
import { Input } from "../components/ui/Input/Input";
import { SearchableSelect } from "../components/ui/SearchableSelect/SearchableSelect";
import type { SearchableSelectOption } from "../components/ui/SearchableSelect/SearchableSelect";
import { Select } from "../components/ui/Select/Select";
import {
	useOfferList,
	useOfferEmailPreview,
	useSendOfferEmail,
	type OfferEmailPreview,
} from "../hooks/use-offers";
import { useBankAccounts } from "../hooks/use-bank-accounts";
import { useAssignableUsers } from "../hooks/use-companies";
import { useAuth } from "../lib/use-auth";
import { api, apiUrl } from "../lib/api";
import { formatDate, formatAmount } from "../lib/date";
import styles from "./Offers.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/offers",
	component: withPermission(OffersPage, "offers", "read"),
	validateSearch: (search: Record<string, unknown>) => ({
		page: Number(search.page) || 1,
		size: Number(search.size) || 20,
		status: String(search.status || ""),
		search: String(search.search || ""),
		companyId: search.companyId ? String(search.companyId) : undefined,
		assignedTo: search.assignedTo ? String(search.assignedTo) : undefined,
		offerDateFrom: String(search.offerDateFrom || ""),
		offerDateTo: String(search.offerDateTo || ""),
		sort: String(search.sort || ""),
		dir: search.dir === "asc" ? ("asc" as const) : ("desc" as const),
	}),
});

function OffersPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const search = route.useSearch();
	const searchQuery = search.search;
	const offerDateFrom = search.offerDateFrom;
	const offerDateTo = search.offerDateTo;
	const status = search.status;
	const companyId = search.companyId;
	const assignedTo = search.assignedTo;
	const page = search.page;
	const pageSize = search.size;
	const sort = search.sort;
	const dir = search.dir;
	const { user } = useAuth();
	const isAdmin = user?.role === "admin";
	const { data: assignableUsers } = useAssignableUsers({ enabled: isAdmin });
	const { data, isLoading, isError, refetch } = useOfferList({
		status: status || undefined,
		search: searchQuery || undefined,
		companyId: companyId || undefined,
		assignedTo: assignedTo || undefined,
		offerDateFrom: offerDateFrom || undefined,
		offerDateTo: offerDateTo || undefined,
		sort: sort || undefined,
		dir,
		page,
		limit: pageSize,
	});
	const preview = useOfferEmailPreview();
	const [sendEmailOfferId, setSendEmailOfferId] = useState<string | null>(null);
	const [emailPreview, setEmailPreview] = useState<OfferEmailPreview | null>(null);
	const [loadingPreviewId, setLoadingPreviewId] = useState<string | null>(null);
	const [companyOption, setCompanyOption] = useState<SearchableSelectOption | undefined>(undefined);

	useEffect(() => {
		if (!companyId) return;
		api
			.get<{ id: string; legalName: string }>(`/api/companies/${companyId}`)
			.then((c) => setCompanyOption({ value: c.id, label: c.legalName }))
			.catch(() => {});
	}, [companyId]);

	const loadCompanies = useCallback(async (query: string) => {
		const res = await api.get<{
			items: Array<{ id: string; legalName: string; currency: string | null }>;
		}>(`/api/companies?limit=50${query ? `&search=${encodeURIComponent(query)}` : ""}`);
		return res.items.map((c) => ({
			value: c.id,
			label: `${c.legalName}${c.currency ? ` (${c.currency})` : ""}`,
		}));
	}, []);

	const openSendEmailModal = (offerId: string) => {
		setLoadingPreviewId(offerId);
		preview.mutate(offerId, {
			onSuccess: (result) => {
				setEmailPreview(result);
				setSendEmailOfferId(offerId);
				setLoadingPreviewId(null);
			},
			onError: (err) => {
				alert(err instanceof Error ? err.message : t("createFailed"));
				setLoadingPreviewId(null);
			},
		});
	};

	const offers = data?.items ?? [];
	const total = data?.total ?? 0;

	const handleExport = () => {
		const params = new URLSearchParams();
		if (status) params.set("status", status);
		if (searchQuery) params.set("search", searchQuery);
		if (companyId) params.set("companyId", companyId);
		if (assignedTo) params.set("assignedTo", assignedTo);
		if (offerDateFrom) params.set("offerDateFrom", offerDateFrom);
		if (offerDateTo) params.set("offerDateTo", offerDateTo);
		if (sort) params.set("sort", sort);
		if (dir) params.set("dir", dir);
		window.open(apiUrl(`/api/offers/export?${params.toString()}`), "_blank");
	};

	const tabs = [
		{ key: "", label: t("all") },
		{ key: "draft", label: t("draft") },
		{ key: "sent", label: t("sent") },
		{ key: "accepted", label: t("accepted") },
		{ key: "rejected", label: t("rejected") },
		{ key: "expired", label: t("expired") },
	];

	return (
		<div>
			<PageHeader
				title={t("offers")}
				action={
					<Link to="/offers/new" className={styles.newLink}>
						<Button>{t("createOffer")}</Button>
					</Link>
				}
			/>

			<div className={styles.filters}>
				<div className={styles.tabs}>
					{tabs.map((tab) => (
						<button
							type="button"
							key={tab.key}
							className={[styles.tab, status === tab.key && styles.active]
								.filter(Boolean)
								.join(" ")}
							onClick={() => {
								navigate({ to: ".", search: { status: tab.key, page: 1, size: pageSize } });
							}}
						>
							{tab.label}
						</button>
					))}
				</div>
			</div>
			<div
				style={{
					display: "flex",
					flexWrap: "wrap",
					alignItems: "flex-start",
					gap: "var(--space-3)",
				}}
			>
				<FilterBar
					filters={[
						{
							key: "search",
							type: "text",
							label: t("search"),
							placeholder: t("offerSearchPlaceholder"),
							width: 380,
						},
						{ key: "offerDate", type: "dateRange", label: t("offerDate") },
						...(isAdmin
							? [
									{
										key: "assignedTo",
										type: "select" as const,
										label: t("assignedTo"),
										options: (assignableUsers ?? []).map((u) => ({
											value: u.id,
											label: u.name,
										})),
									},
								]
							: []),
					]}
					values={{ search: searchQuery, offerDateFrom, offerDateTo, assignedTo: assignedTo ?? "" }}
					onChange={(vals) => {
						navigate({
							to: ".",
							search: {
								...search,
								search: vals.search || "",
								offerDateFrom: vals.offerDateFrom || "",
								offerDateTo: vals.offerDateTo || "",
								assignedTo: vals.assignedTo || undefined,
								page: 1,
								size: pageSize,
							},
						});
					}}
				/>
				<div style={{ width: 280, flexShrink: 0, marginBottom: 16 }}>
					<SearchableSelect
						value={companyId ?? ""}
						onChange={(val) => {
							navigate({
								to: ".",
								search: { ...search, companyId: val || undefined, page: 1, size: pageSize },
							});
						}}
						loadOptions={loadCompanies}
						initialOption={companyOption}
						placeholder={t("company")}
						clearable
					/>
				</div>
				<Button
					variant="secondary"
					onClick={handleExport}
					style={{ marginLeft: "auto", flexShrink: 0 }}
				>
					<Download size={16} />
					{t("exportExcel")}
				</Button>
			</div>

			<Card>
				{isLoading && <LoadingSpinner />}
				{isError && <ErrorFallback onRetry={refetch} />}
				{!isLoading && !isError && (
					<Table
						sortKey={sort || undefined}
						sortDir={dir}
						onSort={(key, nextDir) =>
							void navigate({
								to: "/offers",
								search: { ...search, sort: key, dir: nextDir, page: 1 },
							})
						}
						columns={[
							{
								key: "documentNumber",
								header: t("documentNumber"),
								sortable: true,
								render: (row) => row.documentNumber || row.id.slice(0, 8),
							},
							{
								key: "company",
								header: t("company"),
								render: (row) => row.companyName || row.companyId || "-",
							},
							{
								key: "offerDate",
								header: t("offerDate"),
								sortable: true,
								render: (row) => (row.offerDate ? formatDate(row.offerDate) : "-"),
							},
							{
								key: "validUntil",
								header: t("validUntil"),
								sortable: true,
								render: (row) => (row.validUntil ? formatDate(row.validUntil) : "-"),
							},
							{
								key: "total",
								header: t("total"),
								render: (row) => `${formatAmount(row.total)} ${row.currency || ""}`,
							},
							{
								key: "status",
								header: t("status"),
								render: (row) => <StatusBadge status={row.status} type="offer" />,
							},
							{
								key: "sentAt",
								header: t("sentAt"),
								sortable: true,
								render: (row) => (row.sentAt ? formatDate(row.sentAt) : "-"),
							},
							{
								key: "offerForm",
								header: t("offerForm"),
								render: (row) => (
									<div className={styles.formLinkRow}>
										<button
											type="button"
											className={styles.formLink}
											onClick={() =>
												window.open(apiUrl(`/api/offers/${row.id}/pdf?lang=en`), "_blank")
											}
										>
											EN
										</button>
										<button
											type="button"
											className={styles.formLink}
											onClick={() =>
												window.open(apiUrl(`/api/offers/${row.id}/pdf?lang=tr`), "_blank")
											}
										>
											TR
										</button>
									</div>
								),
							},
							{
								key: "proforma",
								header: t("proforma"),
								render: (row) => (
									<div className={styles.formLinkRow}>
										<button
											type="button"
											className={styles.formLink}
											onClick={() =>
												window.open(
													apiUrl(`/api/offers/${row.id}/pdf?lang=en&variant=proforma`),
													"_blank",
												)
											}
										>
											EN
										</button>
										<button
											type="button"
											className={styles.formLink}
											onClick={() =>
												window.open(
													apiUrl(`/api/offers/${row.id}/pdf?lang=tr&variant=proforma`),
													"_blank",
												)
											}
										>
											TR
										</button>
									</div>
								),
							},
							{
								key: "sendEmail",
								header: t("sendEmail"),
								render: (row) =>
									row.emailSentAt ? (
										<span title={`${t("sent")}: ${formatDate(row.emailSentAt)}`}>
											<Check size={18} className={styles.emailSentIcon} />
										</span>
									) : (
										<button
											type="button"
											className={styles.formLink}
											disabled={loadingPreviewId === row.id}
											onClick={() => openSendEmailModal(row.id)}
										>
											{loadingPreviewId === row.id ? (
												<span className={styles.miniSpinner} />
											) : (
												<Mail size={18} />
											)}
										</button>
									),
							},
							{
								key: "actions",
								header: t("actions"),
								render: (row) => (
									<Link to={`/offers/${row.id}` as never} className={styles.link}>
										{t("viewDetails")}
									</Link>
								),
							},
						]}
						data={offers}
						keyExtractor={(row) => row.id}
					/>
				)}
				<Pagination
					page={page}
					pageSize={pageSize}
					total={total}
					onPageChange={(newPage) =>
						navigate({ to: ".", search: { status, page: newPage, size: pageSize } })
					}
					onPageSizeChange={(newSize) =>
						navigate({ to: ".", search: { status, page: 1, size: newSize } })
					}
				/>
			</Card>

			{sendEmailOfferId && emailPreview && (
				<SendOfferEmailModal
					offerId={sendEmailOfferId}
					preview={emailPreview}
					onClose={() => {
						setSendEmailOfferId(null);
						setEmailPreview(null);
					}}
				/>
			)}
		</div>
	);
}

function SendOfferEmailModal({
	offerId,
	preview,
	onClose,
}: {
	offerId: string;
	preview: OfferEmailPreview;
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const sendEmail = useSendOfferEmail();
	const { data: banks } = useBankAccounts();
	const [docVariant, setDocVariant] = useState<"offer" | "proforma" | null>(null);
	const [bankName, setBankName] = useState("");
	const [currency, setCurrency] = useState("");
	const [showSecondBank, setShowSecondBank] = useState(false);
	const [bankName2, setBankName2] = useState("");
	const [currency2, setCurrency2] = useState("");
	const [bankConfirmed, setBankConfirmed] = useState(false);
	const [email, setEmail] = useState(preview.email);
	const [cc, setCc] = useState(preview.cc);
	const [subject, setSubject] = useState(preview.subject);
	const [message, setMessage] = useState(preview.message);
	const [lang, setLang] = useState<"en" | "tr">("en");
	const [previewLoading, setPreviewLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const selectedBank = banks?.find((b) => b.name === bankName);
	const currencyOptions = selectedBank?.accounts.map((a) => a.currency) ?? [];
	const bankChosen = !!(bankName && currency);

	const selectedBank2 = banks?.find((b) => b.name === bankName2);
	const currencyOptions2 = selectedBank2?.accounts.map((a) => a.currency) ?? [];
	const bank2Chosen = !!(bankName2 && currency2);
	const canContinue = bankChosen && (!showSecondBank || bank2Chosen);

	const changeLang = (next: "en" | "tr") => {
		if (next === lang) return;
		setPreviewLoading(true);
		setLang(next);
	};

	const handleSend = async () => {
		setError(null);
		if (!email.trim()) {
			setError(t("requiredField"));
			return;
		}
		if (preview.signatureMissingReason) {
			const proceed = window.confirm(
				`${preview.signatureMissingReason}\n\nYine de gönderilsin mi?`,
			);
			if (!proceed) return;
		}
		try {
			const result = await sendEmail.mutateAsync({
				id: offerId,
				email: email.trim(),
				cc: cc.trim(),
				subject: subject.trim(),
				message,
				lang,
				variant: docVariant ?? "offer",
				bankName,
				currency,
				...(showSecondBank ? { bankName2, currency2 } : {}),
			});
			if (result.nextcloudSaveWarning) {
				alert(result.nextcloudSaveWarning);
			}
			onClose();
		} catch (err) {
			setError(err instanceof Error ? err.message : t("createFailed"));
		}
	};

	if (!docVariant) {
		return (
			<Modal isOpen onClose={onClose} title={t("sendOfferEmail")} maxWidth="480px">
				<div className={styles.emailModalForm}>
					<p className={styles.emailWarning}>
						Müşteriye teklif formu mu yoksa proforma mı göndermek istiyorsunuz?
					</p>
					<div className={styles.modalActions}>
						<Button variant="ghost" onClick={onClose}>
							{t("cancel")}
						</Button>
						<Button
							variant="secondary"
							onClick={() => {
								setDocVariant("offer");
								setSubject(preview.subject);
							}}
						>
							{t("offerForm")}
						</Button>
						<Button
							onClick={() => {
								setDocVariant("proforma");
								setSubject(preview.subject.replace(/Teklif Formu/i, "Proforma"));
							}}
						>
							{t("proforma")}
						</Button>
					</div>
				</div>
			</Modal>
		);
	}

	if (!bankConfirmed) {
		return (
			<Modal isOpen onClose={onClose} title={t("sendOfferEmail")} maxWidth="480px">
				<div className={styles.emailModalForm}>
					<p className={styles.emailWarning}>
						Formda müşteriye gönderilecek banka hesap bilgilerini seçiniz.
					</p>
					<FormField label="Banka Adı" required>
						<Select
							value={bankName}
							onChange={(e) => {
								setBankName(e.target.value);
								setCurrency("");
							}}
							options={[
								{ value: "", label: "Seçiniz" },
								...(banks ?? []).map((b) => ({ value: b.name, label: b.name })),
							]}
						/>
					</FormField>
					<FormField label="Para Birimi" required>
						<Select
							value={currency}
							onChange={(e) => setCurrency(e.target.value)}
							disabled={!bankName}
							options={[
								{ value: "", label: "Seçiniz" },
								...currencyOptions.map((c) => ({ value: c, label: c })),
							]}
						/>
					</FormField>
					{showSecondBank ? (
						<>
							<FormField label="2. Banka Adı" required>
								<Select
									value={bankName2}
									onChange={(e) => {
										setBankName2(e.target.value);
										setCurrency2("");
									}}
									options={[
										{ value: "", label: "Seçiniz" },
										...(banks ?? []).map((b) => ({ value: b.name, label: b.name })),
									]}
								/>
							</FormField>
							<FormField label="2. Para Birimi" required>
								<Select
									value={currency2}
									onChange={(e) => setCurrency2(e.target.value)}
									disabled={!bankName2}
									options={[
										{ value: "", label: "Seçiniz" },
										...currencyOptions2.map((c) => ({ value: c, label: c })),
									]}
								/>
							</FormField>
							<button
								type="button"
								className={styles.formLink}
								onClick={() => {
									setShowSecondBank(false);
									setBankName2("");
									setCurrency2("");
								}}
							>
								İkinci bankayı kaldır
							</button>
						</>
					) : (
						<button
							type="button"
							className={styles.formLink}
							onClick={() => setShowSecondBank(true)}
						>
							+ İkinci banka ekle
						</button>
					)}
					<div className={styles.modalActions}>
						<Button variant="ghost" onClick={onClose}>
							{t("cancel")}
						</Button>
						<Button disabled={!canContinue} onClick={() => setBankConfirmed(true)}>
							Devam Et
						</Button>
					</div>
				</div>
			</Modal>
		);
	}

	return (
		<Modal isOpen onClose={onClose} title={t("sendOfferEmail")} maxWidth="1100px">
			<div className={styles.emailModalLayout}>
				<div className={styles.emailModalForm}>
					<FormField label="Belge Türü">
						<div className={styles.formLinkRow}>
							<span>{docVariant === "proforma" ? t("proforma") : t("offerForm")}</span>
							<button
								type="button"
								className={styles.formLink}
								onClick={() => {
									setDocVariant(null);
									setPreviewLoading(true);
								}}
							>
								Değiştir
							</button>
						</div>
					</FormField>
					<FormField label={t("offerForm")}>
						<div className={styles.formLinkRow}>
							<button
								type="button"
								className={[styles.langButton, lang === "en" && styles.langButtonActive]
									.filter(Boolean)
									.join(" ")}
								onClick={() => changeLang("en")}
							>
								EN
							</button>
							<button
								type="button"
								className={[styles.langButton, lang === "tr" && styles.langButtonActive]
									.filter(Boolean)
									.join(" ")}
								onClick={() => changeLang("tr")}
							>
								TR
							</button>
						</div>
					</FormField>
					<FormField label={t("email")} required>
						<Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
					</FormField>
					<FormField label={t("emailCc")}>
						<Input
							value={cc}
							onChange={(e) => setCc(e.target.value)}
							placeholder={t("emailCcPlaceholder")}
						/>
					</FormField>
					<FormField label={t("subject")}>
						<Input value={subject} onChange={(e) => setSubject(e.target.value)} />
					</FormField>
					<FormField label={t("message")}>
						<textarea
							className={styles.textarea}
							rows={8}
							value={message}
							onChange={(e) => setMessage(e.target.value)}
						/>
					</FormField>
					{preview.noNextcloud && (
						<p className={styles.emailWarning}>{t("nextcloudNotConnected")}</p>
					)}
					{preview.foundDatasheets.length > 0 && (
						<div className={styles.datasheetList}>
							<p>{t("attachedDatasheets")}</p>
							<ul>
								{preview.foundDatasheets.map((d) => (
									<li key={d.path}>
										<a
											href={apiUrl(`/api/nextcloud/file?path=${encodeURIComponent(d.path)}`)}
											target="_blank"
											rel="noreferrer"
										>
											{d.description}
										</a>
									</li>
								))}
							</ul>
						</div>
					)}
					{preview.missingDatasheets.length > 0 && (
						<div className={styles.emailWarning}>
							<p>{t("missingDatasheetsWarning")}</p>
							<ul>
								{preview.missingDatasheets.map((d) => (
									<li key={d}>{d}</li>
								))}
							</ul>
						</div>
					)}
					{preview.excludedDatasheets.length > 0 && (
						<div className={styles.emailWarning}>
							<p>{t("excludedDatasheetsWarning")}</p>
							<ul>
								{preview.excludedDatasheets.map((d) => (
									<li key={d}>{d}</li>
								))}
							</ul>
						</div>
					)}
					{error && <p className={styles.emailError}>{error}</p>}
					<div className={styles.modalActions}>
						<Button variant="ghost" onClick={onClose}>
							{t("cancel")}
						</Button>
						<Button onClick={handleSend} isLoading={sendEmail.isPending}>
							{t("send")}
						</Button>
					</div>
				</div>
				<div className={styles.emailModalPreview}>
					{previewLoading && (
						<div className={styles.previewLoading}>
							<LoadingSpinner />
						</div>
					)}
					<iframe
						key={`${lang}-${docVariant}`}
						src={apiUrl(
							`/api/offers/${offerId}/pdf?lang=${lang}&bankName=${encodeURIComponent(bankName)}&currency=${encodeURIComponent(currency)}` +
								(docVariant === "proforma" ? "&variant=proforma" : "") +
								(showSecondBank
									? `&bankName2=${encodeURIComponent(bankName2)}&currency2=${encodeURIComponent(currency2)}`
									: ""),
						)}
						title="offer-pdf-preview"
						className={styles.previewFrame}
						onLoad={() => setPreviewLoading(false)}
					/>
				</div>
			</div>
		</Modal>
	);
}
